import React, { useState } from 'react';
import { ChevronLeft, ChevronRight, Maximize2 } from 'lucide-react';

const HEADING_FONT = 'Manrope, sans-serif';

/**
 * Image Gallery Block Renderer
 * Variants: grid, carousel, lightbox
 */
export default function GalleryRenderer({ props, variant, isEditing }) {
  const {
    headline = '',
    images = [],
    columns = 3,
    gap = 'md',
    rounded = true,
  } = props;

  const gapClass = { sm: 'gap-2', md: 'gap-4', lg: 'gap-6' }[gap] || 'gap-4';
  const colsClass = {
    2: 'grid-cols-1 sm:grid-cols-2',
    3: 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-3',
    4: 'grid-cols-2 sm:grid-cols-3 lg:grid-cols-4',
  }[columns] || 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-3';
  const roundedClass = rounded ? 'rounded-xl' : '';

  const headerEl = headline ? (
    <div className="text-center mb-10">
      <h2
        className="text-3xl sm:text-4xl font-bold text-stone-900 mb-3"
        style={{ fontFamily: HEADING_FONT }}
      >
        {headline}
      </h2>
    </div>
  ) : null;

  if (variant === 'carousel') {
    return (
      <section className="py-16 px-6">
        <div className="max-w-4xl mx-auto">
          {headerEl}
          <CarouselGallery images={images} roundedClass={roundedClass} />
        </div>
      </section>
    );
  }

  if (variant === 'lightbox') {
    return (
      <section className="py-16 px-6">
        <div className="max-w-6xl mx-auto">
          {headerEl}
          <div className={`grid ${colsClass} ${gapClass}`}>
            {images.map((img, i) => (
              <div key={i} className={`relative group overflow-hidden ${roundedClass}`}>
                <img
                  src={img.image_url || 'https://placehold.co/600x400/e7e5e4/78716c?text=Image'}
                  alt={img.alt_text || ''}
                  className={`w-full aspect-[4/3] object-cover ${roundedClass}`}
                />
                <div className="absolute inset-0 bg-black/0 group-hover:bg-black/30 transition-colors flex items-center justify-center">
                  <Maximize2 className="w-6 h-6 text-white opacity-0 group-hover:opacity-100 transition-opacity" />
                </div>
                {img.caption && (
                  <p className="mt-2 text-xs text-stone-500 text-center" style={{ fontFamily: 'Inter, sans-serif' }}>
                    {img.caption}
                  </p>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>
    );
  }

  // Grid variant (default)
  return (
    <section className="py-16 px-6">
      <div className="max-w-6xl mx-auto">
        {headerEl}
        <div className={`grid ${colsClass} ${gapClass}`}>
          {images.map((img, i) => (
            <div key={i}>
              <img
                src={img.image_url || 'https://placehold.co/600x400/e7e5e4/78716c?text=Image'}
                alt={img.alt_text || ''}
                className={`w-full aspect-[4/3] object-cover ${roundedClass}`}
              />
              {img.caption && (
                <p className="mt-2 text-xs text-stone-500 text-center" style={{ fontFamily: 'Inter, sans-serif' }}>
                  {img.caption}
                </p>
              )}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function CarouselGallery({ images, roundedClass }) {
  const [current, setCurrent] = useState(0);

  if (!images.length) {
    return (
      <div className={`aspect-video bg-stone-100 ${roundedClass} flex items-center justify-center`}>
        <p className="text-sm text-stone-400">Add images to this gallery</p>
      </div>
    );
  }

  const prev = () => setCurrent((c) => (c === 0 ? images.length - 1 : c - 1));
  const next = () => setCurrent((c) => (c === images.length - 1 ? 0 : c + 1));

  return (
    <div className="relative">
      <div className={`relative overflow-hidden ${roundedClass}`}>
        <img
          src={images[current]?.image_url || 'https://placehold.co/800x500/e7e5e4/78716c?text=Image'}
          alt={images[current]?.alt_text || ''}
          className={`w-full aspect-video object-cover ${roundedClass}`}
        />
        {images[current]?.caption && (
          <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/60 to-transparent p-4">
            <p className="text-white text-sm" style={{ fontFamily: 'Inter, sans-serif' }}>
              {images[current].caption}
            </p>
          </div>
        )}
      </div>

      {/* Navigation arrows */}
      {images.length > 1 && (
        <>
          <button
            onClick={prev}
            className="absolute left-3 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-white/90 shadow-md flex items-center justify-center hover:bg-white transition-colors"
          >
            <ChevronLeft className="w-5 h-5 text-stone-700" />
          </button>
          <button
            onClick={next}
            className="absolute right-3 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-white/90 shadow-md flex items-center justify-center hover:bg-white transition-colors"
          >
            <ChevronRight className="w-5 h-5 text-stone-700" />
          </button>
        </>
      )}

      {/* Dot indicators */}
      {images.length > 1 && (
        <div className="flex items-center justify-center gap-2 mt-4">
          {images.map((_, i) => (
            <button
              key={i}
              onClick={() => setCurrent(i)}
              className={`w-2 h-2 rounded-full transition-colors ${
                i === current ? 'bg-stone-900' : 'bg-stone-300'
              }`}
            />
          ))}
        </div>
      )}
    </div>
  );
}
